import Forest from "lib/reauth/Forest";
import OrgSelect from "lib/reauth/OrgSelect";
import parseSpec from "lib/reauth/parseSpec";
import {
    useArmusExists,
    useImpls,
    useOrgs,
} from "lib/reauth/ReauthModule";
import Spec from "lib/reauth/Spec";
import updateForestForImpls from "lib/reauth/updateForestForImpls";
import useSpec from "lib/reauth/useSpec";
import PropTypes from "prop-types";
import React from "react";

const ActiveOrgContext = React.createContext(undefined);

export function isActiveOrg(name) {
    return React.useContext(ActiveOrgContext) === name;
}

function FormItem({
                      label,
                      children,
                  }) {
    return <div className={"flex"}>
        <label className={"flex-label"}>
            {label}:
        </label>
        {children}
    </div>;
}

FormItem.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.element,
};

const ORG_ARMUS = "armus";

function newKid(name, disallowAccess) {
    const kid = {
        name,
        kids: [],
    };
    if (disallowAccess) {
        kid.perms = ["NONE"];
    }
    return kid;
}

function Form({cookie, onSave, onCancel}) {
    const [user, setUser] = React.useState("fred");
    const [activeOrg, setActiveOrg] = React.useState("");
    const [forest, setForest] = React.useState([]);
    const armusExists = useArmusExists();
    const orgs = useOrgs();
    const impls = useImpls();
    const users = React.useMemo(() => {
        const result = [
            { id: "fred", label: "fred" },
            { id: "admin", label: "admin (a superuser)" },
        ];
        if (user && result.every(r => r.id !== user)) {
            result.unshift({ id: user, label: user });
        }
        return result;
    }, [ user ]);

    function handleTextUpdate(curr) {
        if (!curr) return;
        const [ user, org, forestUpdater ] = parseSpec(curr);
        user && setUser(user);
        org && setActiveOrg(org);
        setForest(forestUpdater);
    }

    function buildImplLicense(implementer, activeOrg) {
        const kid = newKid(implementer, activeOrg !== implementer);
        if (activeOrg !== implementer) {
            let lowest = kid;
            if (implementer !== ORG_ARMUS && armusExists) {
                lowest = newKid(ORG_ARMUS, activeOrg !== ORG_ARMUS);
                kid.kids.push(lowest);
            }
            if (activeOrg !== ORG_ARMUS) {
                lowest.kids.push(newKid(activeOrg));
            }
        }
        return kid;
    }

    function handleGuess() {
        const badOrgs = new Set();
        badOrgs.add("armus");
        badOrgs.add("armus-admin");
        forest.forEach(it => badOrgs.add(it.implementer));
        let org = activeOrg;
        if (badOrgs.has(org)) {
            // see if there's a better one...
            const o = orgs.find(it => !badOrgs.has(it));
            if (o) {
                org = o;
                setActiveOrg(org);
            }
        }
        setForest(frst => {
            const impl = frst
                .map(it => [
                    it.name === "adt" ? 2 : 1,
                    it.name === "hemo" ? 2 : 1,
                    it.implementer === "armus" ? 2 : 1,
                    it.implementer === "armus-admin" ? 2 : 1,
                    it.name,
                ].join(","))
                .sort()
            .map(it => it.split(",")[4])[0];
            const idx = frst.findIndex(it => it.name === impl);
            return frst.map((it, i) => ({
                ...it,
                kids: i === idx
                    ? [buildImplLicense(impl, org)]
                    : [],
            }));
        });
    }

    React.useEffect(
        () => handleTextUpdate(cookie),
        [ cookie ],
    );

    React.useEffect(
        () => {
            if (!orgs) return;
            setActiveOrg(o => o || orgs[0]);
        },
        [orgs],
    );
    React.useEffect(
        () => {
            if (!impls) return;
            setForest(f => updateForestForImpls(f, impls));
        },
        [impls],
    );

    const [spec, valid, ancestry] = useSpec(user, activeOrg, forest);

    return <React.Fragment>
        {cookie && <FormItem label="Cookie">
            <Spec
                spec={cookie}
                collapsed
            />
        </FormItem>}
        <FormItem label="Username">
            <select
                value={user}
                onChange={e => setUser(e.target.value)}
            >
                {users.map(u =>
                    <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
        </FormItem>
        <FormItem label="Active Org">
            <OrgSelect
                value={activeOrg}
                onChange={e => setActiveOrg(e.target.value)}
            />
        </FormItem>
        <FormItem label="Licenses" />
        <ActiveOrgContext.Provider value={activeOrg}>
            <Forest
                forest={forest}
                ancestry={ancestry}
                onChange={setForest}
                buildImplLicense={impl => buildImplLicense(impl, activeOrg)}
            />
        </ActiveOrgContext.Provider>
        <FormItem label="Spec">
            <Spec
                spec={spec}
                onChange={handleTextUpdate}
            />
        </FormItem>
        <div className="modal-footer">
            <button
                className="btn btn-save"
                disabled={!valid}
                onClick={() => onSave(spec)}
            >
                <span /> Reauthorize
            </button>
            {cookie && <button
                className="btn btn-refresh"
                disabled={cookie === spec}
                onClick={() => handleTextUpdate(cookie)}
                title={"Restore the authorization from your cookie"}
            >
                <span /> Reset
            </button>}
            <button
                className="btn btn-refresh"
                onClick={handleGuess}
                title={"Guess a likely authorization structure."}
            >
                <span /> Guess?
            </button>
            <button
                className="btn btn-cancel"
                onClick={onCancel}
            >
                <span /> Cancel
            </button>
        </div>
    </React.Fragment>;
}

Form.propTypes = {
    cookie: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default Form;



// WEBPACK FOOTER //
// ./app/lib/reauth/Form.js